import React, { useEffect, useState } from "react";

import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import "./app.css";

import { identifyAnalyticsUser, resetAnalyticsUser } from "./utils/analytics";
import { getProfile } from "./utils/account";

import Home from "./components/Home/index";
import Login from "./components/Login/index";
import Workspace from "./components/Workspace/WorkspaceInterface";
import LeftMenu from "./components/LeftMenu";
import Photogenix from "./components/Photogenix";
import Models from "./components/Photogenix/Models";
import Backgrounds from "./components/Photogenix/Backgrounds";
import Header from "./components/Header";
import ProfileInterface from "./components/Profile/ProfileInterface";
import RightSideApps from "./components/Services/RightSideApps";
import Billing from "./components/Billing";

import Access from "./components/Access";
import Teams from "./components/Teams";
import BillingPlans from "./components/BillingPlans";
import InviteUserModal from "./components/InviteUserModal";
import NotificationToast from "./components/commonComponents/NotificationToast";
import Library from "./components/Library";
import Billing2 from "./components/Billing2";
import { generateUniqueId, notify } from "./utils/utils";
import TermsAndConditions from "./components/TermsAndConditions";
import Returns from "./components/Returns";
import Cookies from "./components/Cookies";
import Privacy from "./components/Privacy";
import { apiCall } from "./utils/api";
import { handleOnLogin } from "./globalActions";
import UserWorkspace from "./components/UserWorkspace/UserWorkspace";
import Services from "./components/Service";
import BillingActivity from "./components/BillingActivity";
import RollOver from "./components/RollOver";
import TrackUsage from "./components/TrackUsage";
import Subscriptions from "./components/Subscriptions";
import { getDashboardVersion } from "./utils/dashboardDetails";
import UserSpecializations from "./components/UserSpecializations";
import StripeProducts from "./components/StripeProducts";
import PaidUsers from "./components/PaidUsers";
import BillingAlerts from "./components/BillingAlerts";
import Organizations from "./components/Organizations/index";
import OrganizationDetail from "./components/Organizations/OrganizationDetail";
import WorkspaceOrgMigration from "./components/WorkspaceOrgMigration/index";
import DataCleanup from "./components/DataCleanup/index";

const AIGatewayConsole = React.lazy(() =>
  import("./components/AIGateway").then((module) => ({
    default: module.AIGatewayConsole,
  }))
);

export const RequireAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const isLeftMenuCollpsed = useSelector(
    (store: any) => store.leftMenuReducer.isLeftMenuCollpsed,
  );
  const userPreferences = useSelector(
    (store: any) => store.globalReducer.userPreferences,
  );

  return (
    <>
      <div className="app-container">
        <div className="app-background-blur">
          <div className="app-background-image" />
        </div>
        <div className="app-layout-wrapper">
          {/* <Header></Header> */}
          <div className="app-content-wrapper">
            <LeftMenu></LeftMenu>
            <div
              className="app-layout-wrapper"
              style={{
                width:
                  userPreferences && userPreferences.sidebarIsCollapsed
                    ? "calc(100% - 64px)"
                    : "calc(100% - 256px)",
              }}
            >
              <div
                className={`app-content-section ${
                  isLeftMenuCollpsed ? "app-content-section-collapsed" : ""
                }`}
              >
                {children}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const MemberAccessAuth: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const selectedWorkspace = useSelector(
    (state: any) => state.globalReducer.selectedWorkspace,
  );

  const [loading, setLoading] = useState(true);
  console.log("checking role");

  useEffect(() => {
    if (selectedWorkspace) {
      setLoading(false);
      if (selectedWorkspace.role == "member")
        notify("Members has no access to this section", "error");
    }
  }, [selectedWorkspace]);

  if (loading || !selectedWorkspace) {
    return <>Loading...</>;
  }

  return selectedWorkspace && selectedWorkspace.role !== "member" ? (
    <>{children}</>
  ) : (
    <Navigate to="/workspace" replace />
  );
};
let INTERVAL_ID;
export function pingUserToServer() {
  let deviceId;
  if (window.localStorage.getItem("deviceId")) {
    deviceId = window.localStorage.getItem("deviceId");
  } else {
    deviceId = generateUniqueId(12);
    window.localStorage.setItem("deviceId", deviceId);
  }
  apiCall("/api/v1/ping", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId }),
  }).catch((err) => {
    if (
      err &&
      err.status &&
      err.status.message &&
      err.status.message.active === false
    ) {
      console.log(err);
      document.getElementById("logout-prompt").style.display = "flex";
      clearInterval(INTERVAL_ID);
      resetAnalyticsUser();
      window.localStorage.clear();
    }
  });
}
export default function App() {
  const dispatch = useDispatch();

  useEffect(() => {
    const initializeApp = async () => {
      try {
        const user = await getProfile();
        getDashboardVersion()
          .then((res) => {
            console.log("Dashboard Version on App Load:", res);
          })
          .catch((err) => {
            console.log("Error fetching dashboard version on App Load:", err);
          });
        if (user) {
          const userData = user.data;
          const parsedUserData = userData;
          // INTERVAL_ID = setInterval(pingUserToServer, 600); // Ping every 1 minute
          dispatch(handleOnLogin(parsedUserData));

          identifyAnalyticsUser({ id: parsedUserData._id });
        }
        const params = new URLSearchParams(window.location.search);
        const referralCode = params.get("referral_code");

        if (referralCode) {
          localStorage.setItem("referral_code", referralCode);
        }
      } catch (err) {
        console.log("Error: ", err);
      }
    };

    void initializeApp();

    return () => {
      if (INTERVAL_ID) {
        clearInterval(INTERVAL_ID);
      }
    };
  }, [dispatch]);

  return (
    <>
      <NotificationToast></NotificationToast>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path={"/terms-and-conditions"}
            element={<TermsAndConditions></TermsAndConditions>}
          />
          <Route path={"/privacy"} element={<Privacy></Privacy>} />
          <Route path={"/returns"} element={<Returns></Returns>} />
          <Route path={"/cookies"} element={<Cookies></Cookies>} />

          <Route
            path="/profile"
            element={
              <RequireAuth>
                <ProfileInterface />
              </RequireAuth>
            }
          />
          <Route
            path="/teams"
            element={
              <RequireAuth>
                <Teams />
              </RequireAuth>
            }
          />
          <Route
            path="/workspace"
            element={
              <RequireAuth>
                <Workspace />
              </RequireAuth>
            }
          />
          <Route
            path="/photogenix"
            element={
              <RequireAuth>
                <Photogenix />
              </RequireAuth>
            }
          />
          <Route
            path="/photogenix/models"
            element={
              <RequireAuth>
                <Models />
              </RequireAuth>
            }
          />
          <Route
            path="/photogenix/backgrounds"
            element={
              <RequireAuth>
                <Backgrounds />
              </RequireAuth>
            }
          />

          <Route
            path="/photogenix/poses"
            element={
              <RequireAuth>
                <Photogenix />
              </RequireAuth>
            }
          />

          <Route
            path="/photogenix/stylings"
            element={
              <RequireAuth>
                <Photogenix />
              </RequireAuth>
            }
          />

          <Route
            path="/photogenix/ad-stylings"
            element={
              <RequireAuth>
                <Photogenix />
              </RequireAuth>
            }
          />

          <Route
            path="/photogenix/product-bgs"
            element={
              <RequireAuth>
                <Photogenix />
              </RequireAuth>
            }
          />

          <Route
            path="/user/:userId/workspace"
            element={
              <RequireAuth>
                <Workspace />
              </RequireAuth>
            }
          />

          <Route
            path="/billing/:workspaceId"
            element={
              <RequireAuth>
                <Billing />
              </RequireAuth>
            }
          />

          <Route
            path="/access/:workspaceId"
            element={
              <RequireAuth>
                <Access />
              </RequireAuth>
            }
          />

          <Route
            path="/service"
            element={
              <RequireAuth>
                <Services />
              </RequireAuth>
            }
          />
          <Route
            path="/user-specializations"
            element={
              <RequireAuth>
                <UserSpecializations />
              </RequireAuth>
            }
          />

          <Route
            path="/paid-users"
            element={
              <RequireAuth>
                <PaidUsers />
              </RequireAuth>
            }
          />

          <Route
            path="/billing-alerts"
            element={
              <RequireAuth>
                <BillingAlerts />
              </RequireAuth>
            }
          />

          <Route
            path="/subscriptions"
            element={
              <RequireAuth>
                <Subscriptions />
              </RequireAuth>
            }
          />

          <Route
            path="/stripe-products"
            element={
              <RequireAuth>
                <StripeProducts />
              </RequireAuth>
            }
          />

          <Route
            path="/activity/workspace/:workspaceId"
            element={
              <RequireAuth>
                <BillingActivity />
              </RequireAuth>
            }
          />

          <Route
            path="/roll-over/:workspaceId"
            element={
              <RequireAuth>
                <RollOver />
              </RequireAuth>
            }
          />

          <Route
            path="/track-usage/:workspaceId"
            element={
              <RequireAuth>
                <TrackUsage />
              </RequireAuth>
            }
          />

          <Route
            path="/organizations"
            element={
              <RequireAuth>
                <Organizations />
              </RequireAuth>
            }
          />

          <Route
            path="/organizations/:organizationId"
            element={
              <RequireAuth>
                <OrganizationDetail />
              </RequireAuth>
            }
          />

          <Route
            path="/workspace-org-migration"
            element={
              <RequireAuth>
                <WorkspaceOrgMigration />
              </RequireAuth>
            }
          />

          <Route
            path="/data-cleanup"
            element={
              <RequireAuth>
                <DataCleanup />
              </RequireAuth>
            }
          />

          <Route
            path="/ai-gateway/*"
            element={
              <RequireAuth>
                <React.Suspense fallback={<div>Loading AI Gateway…</div>}>
                  <AIGatewayConsole />
                </React.Suspense>
              </RequireAuth>
            }
          />

          <Route path="*" element={<Navigate to="/teams" replace />} />
        </Routes>
      </Router>
    </>
  );
}
